/**
 * BuchhaltungGutschriften — Gutschriften (alacak dekontları).
 *
 * Erstellung: Rechnung wählen → Positionen der FESTGESCHRIEBENEN Rechnung
 * (mit Kundenrabatt + Steuersatz der Rechnung) → Mengen eingeben →
 * fibu_gutschrift_create bucht atomar: GS-Nummer, Erlösschmälerung (4720/
 * 4710) + USt-Korrektur an Debitor, offener Posten gemindert bzw. bei
 * bezahlter Rechnung Kundenguthaben. Aufruf aus Rechnungen: ?invoice=<id>.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FilePlus2, Search, Download, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/api/AuthContext';
import { computeTaxGroups } from '@/lib/invoiceCalc';
import { listInvoices, getInvoiceDetail, createGutschrift as createGutschriftRpc } from '@/lib/invoiceApi';
import { downloadGutschriftPdf } from '@/components/orders/GutschriftPDF';
import { fmtEUR } from '@/lib/buchhaltungApi';

export default function BuchhaltungGutschriften() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [quantities, setQuantities] = useState({}); // pos → Menge
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: gutschriften = [], isLoading } = useQuery({
    queryKey: ['gutschriften'],
    queryFn: () => base44.entities.Gutschrift.list('-created_at'),
  });
  const { data: invoices } = useQuery({ queryKey: ['fibu-invoices'], queryFn: listInvoices });
  const creditable = useMemo(
    () => (invoices || []).filter((i) => i.kind === 'invoice' && ['open', 'partial', 'paid'].includes(i.status)),
    [invoices],
  );
  const { data: invoice } = useQuery({
    queryKey: ['fibu-invoice', Number(selectedInvoiceId)],
    queryFn: () => getInvoiceDetail(Number(selectedInvoiceId)),
    enabled: !!selectedInvoiceId,
  });

  // Aus Rechnungen kommend: ?invoice=<id> (alt: ?order=<auftrag>)
  useEffect(() => {
    const invParam = searchParams.get('invoice');
    const orderParam = searchParams.get('order');
    if (!invParam && !orderParam) return;
    if (invParam) {
      setSelectedInvoiceId(invParam);
    } else if (invoices) {
      const inv = invoices.find((i) => i.order_id === orderParam && i.kind === 'invoice' && i.status !== 'cancelled');
      if (inv) setSelectedInvoiceId(String(inv.id));
    } else {
      return;
    }
    setShowForm(true);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, invoices]);

  const lines = invoice?.lines || [];
  const gsLines = lines
    .map((l) => {
      const qty = Math.min(Number(String(quantities[l.pos] ?? '').replace(',', '.')) || 0, Number(l.quantity));
      return qty > 0 ? { pos: l.pos, quantity: qty, unit_price: Number(l.unit_price_net), rabatt_prozent: Number(l.discount_pct), tax_rate: Number(l.tax_rate) } : null;
    })
    .filter(Boolean);
  const preview = computeTaxGroups(gsLines, { kundenrabattProzent: Number(invoice?.kundenrabatt_prozent) || 0 });
  const subtotal = preview.netto;
  const taxAmount = preview.taxAmount;
  const total = preview.total;

  const createGutschrift = async () => {
    if (!invoice || gsLines.length === 0) return;
    setSaving(true);
    try {
      const gs = await createGutschriftRpc(invoice.id, gsLines.map((l) => ({ pos: l.pos, quantity: l.quantity })), reason || null, user?.email);
      await downloadGutschriftPdf({
        ...gs, invoice_no: invoice.invoice_no, lead_name: invoice.customer_name, reason, created_at: new Date().toISOString(),
      });
      queryClient.invalidateQueries();
      toast.success(`Gutschrift ${gs.gutschrift_no} über ${fmtEUR(gs.total)} gebucht${gs.guthaben > 0 ? ` — ${fmtEUR(gs.guthaben)} als Kundenguthaben (Rechnung war bezahlt)` : ''}.`);
      setShowForm(false);
      setSelectedInvoiceId('');
      setQuantities({});
      setReason('');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const t = searchTerm.toLowerCase();
    return gutschriften.filter((g) =>
      !t ||
      (g.lead_name || '').toLowerCase().includes(t) ||
      (g.gutschrift_no || '').toLowerCase().includes(t) ||
      (g.invoice_no || '').toLowerCase().includes(t)
    );
  }, [gutschriften, searchTerm]);

  return (
    <PageShell
      head={(
        <div className="flex items-center justify-between gap-2">
          <PageHeader
            icon={<FilePlus2 className="w-5 h-5" />}
            title="Gutschriften"
            subtitle={`${gutschriften.length} Gutschriften`}
          />
          <Button onClick={() => setShowForm(true)}
            className="h-11 bg-slate-900 hover:bg-slate-800 gap-1.5 text-xs font-bold">
            <Plus className="w-4 h-4" /> Gutschrift
          </Button>
        </div>
      )}
    >

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Kunde, GS- oder Rechnungsnummer…"
          className="pl-10 h-11 bg-white"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Liste */}
      <Card className="overflow-hidden">
        <div className="hidden md:grid grid-cols-[120px_120px_1fr_100px_110px_48px] gap-2 px-3 py-2.5 bg-slate-50 border-b text-[10px] font-bold uppercase text-slate-500">
          <span>Gutschrift</span><span>Zu Rechnung</span><span>Kunde</span>
          <span>Datum</span><span className="text-right">Betrag</span><span />
        </div>
        {isLoading && <p className="p-10 text-center text-sm text-slate-400"><Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" /> Lade…</p>}
        {!isLoading && filtered.length === 0 && (
          <p className="p-10 text-center text-sm text-slate-400">
            Noch keine Gutschriften. Über „Gutschrift“ oder das Rechnungs-Menü erstellen.
          </p>
        )}
        <div className="divide-y divide-slate-100">
          {filtered.map((g) => (
            <div key={g.id} className="px-3 py-2.5 md:grid md:grid-cols-[120px_120px_1fr_100px_110px_48px] md:gap-2 md:items-center">
              <span className="block font-mono text-xs font-bold text-slate-700">{g.gutschrift_no}</span>
              <span className="block font-mono text-xs text-slate-500">{g.invoice_no || '—'}</span>
              <span className="block min-w-0">
                <span className="block text-sm font-semibold text-slate-900 truncate">{g.lead_name || '—'}</span>
                <span className="block text-[11px] text-slate-400 truncate">
                  {(g.items || []).length} Position(en){g.reason ? ` · ${g.reason}` : ''}
                </span>
              </span>
              <span className="block text-xs text-slate-600">{new Date(g.created_at).toLocaleDateString('de-DE')}</span>
              <span className="block text-xs font-mono font-bold text-red-600 md:text-right">−{fmtEUR(g.total)}</span>
              <span className="flex justify-end">
                <Button size="icon" variant="ghost" aria-label="Gutschrift herunterladen"
                  className="h-9 w-9 text-slate-500 hover:text-slate-900"
                  onClick={() => downloadGutschriftPdf(g)}>
                  <Download className="w-4 h-4" />
                </Button>
              </span>
            </div>
          ))}
        </div>
      </Card>

      {/* Oluşturma formu */}
      <Sheet open={showForm} onOpenChange={setShowForm}>
        <SheetContent side="bottom" className="h-[92vh] overflow-y-auto">
          <SheetHeader className="mb-4"><SheetTitle>Gutschrift erstellen</SheetTitle></SheetHeader>
          <div className="space-y-4 max-w-2xl mx-auto pb-8">
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-500">Rechnung auswählen</label>
              <Select value={selectedInvoiceId} onValueChange={(v) => { setSelectedInvoiceId(v); setQuantities({}); }}>
                <SelectTrigger className="h-11 text-xs"><SelectValue placeholder="Rechnung wählen…" /></SelectTrigger>
                <SelectContent>
                  {creditable.map((i) => (
                    <SelectItem key={i.id} value={String(i.id)} className="text-xs">
                      {i.invoice_no} · {i.customer_name} · {fmtEUR(i.gross_total)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {invoices === null && <p className="text-xs text-amber-700 mt-1">Rechnungsmodul noch nicht eingerichtet (Migration 20261016).</p>}
            </div>

            {invoice && (
              <>
                <div className="space-y-1.5">
                  <p className="text-[10px] font-bold uppercase text-slate-500">
                    Positionen — gutzuschreibende Menge eingeben
                  </p>
                  {lines.map((l) => (
                    <div key={l.pos} className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-800 truncate">{l.description}</p>
                        <p className="text-[10px] text-slate-400 tabular-nums">
                          {Number(l.quantity).toLocaleString('de-DE')}x à {Number(l.unit_price_net).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} € · USt {Number(l.tax_rate)} %
                          {Number(l.discount_pct) > 0 && ` · −${Number(l.discount_pct)} %`}
                        </p>
                      </div>
                      <Input
                        type="number" inputMode="decimal" min="0" max={Number(l.quantity)}
                        placeholder="0"
                        aria-label={`Menge für ${l.description}`}
                        value={quantities[l.pos] ?? ''}
                        onChange={(e) => setQuantities({ ...quantities, [l.pos]: e.target.value })}
                        className="h-11 w-24 text-right text-sm font-bold"
                      />
                    </div>
                  ))}
                  {Number(invoice.kundenrabatt_prozent) > 0 && (
                    <p className="text-[11px] text-orange-700">Kundenrabatt der Rechnung ({Number(invoice.kundenrabatt_prozent)} %) wird berücksichtigt.</p>
                  )}
                </div>

                <Input
                  placeholder="Grund (optional, z.B. Retoure, Bruch)"
                  value={reason} onChange={(e) => setReason(e.target.value)}
                  className="h-11 text-sm"
                />

                <div className={cn(
                  'p-3 rounded-lg border text-xs font-bold flex items-center justify-between',
                  total > 0 ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-slate-50 border-slate-200 text-slate-500'
                )}>
                  <span>Netto −{fmtEUR(subtotal)} · USt −{fmtEUR(taxAmount)}</span>
                  <span>Gutschrift: −{fmtEUR(total)}</span>
                </div>

                <Button
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 font-bold"
                  disabled={total <= 0 || saving}
                  onClick={createGutschrift}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Gutschrift über ${fmtEUR(total)} erstellen`}
                </Button>
                <p className="text-[10px] text-slate-400 text-center">
                  Erstellt GS-Beleg (PDF), bucht die Erlösschmälerung mit USt-Korrektur und
                  mindert den offenen Posten — bei bezahlter Rechnung entsteht ein Kundenguthaben.
                </p>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </PageShell>
  );
}
